import type { RecordingInfo } from '@/lib/utils/tauri-recording'
import { Button } from '@/components/ui/button'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { CircleNotchIcon, RecordIcon } from '@phosphor-icons/react'

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

function formatStartTime(startedAt: string): string {
  const date = new Date(startedAt)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

export type RecordingRecoveryAction = 'keep' | 'finish' | 'discard'

/**
 * Shown when the app comes back to the workspace of a recording that was cut
 * off by an app quit or crash. The audio and notes are safe on disk; the user
 * continues the same recording, finishes it (upload + AI notes), or discards
 * it. Closing the dialog decides later: it asks again next launch.
 */
export function RecordingRecoveryDialog({
  recording,
  hasNotes,
  busyAction,
  onAction,
  onLater,
}: {
  recording: RecordingInfo | null
  hasNotes: boolean
  busyAction: RecordingRecoveryAction | null
  onAction: (action: RecordingRecoveryAction) => void
  onLater: () => void
}) {
  const startTime = recording ? formatStartTime(recording.startedAt) : ''
  const isBusy = busyAction !== null
  return (
    <DialogPattern
      open={!!recording}
      onOpenChange={open => {
        if (!open && !isBusy) onLater()
      }}
      title="Your recording was interrupted"
      description={
        recording
          ? `Scrambl closed while you were recording${startTime ? ` (started ${startTime})` : ''}. ${formatDuration(recording.durationMs)} of audio${hasNotes ? ' and your notes are' : ' is'} saved on this computer.`
          : undefined
      }
      icon={<RecordIcon className="h-5 w-5 text-red-500" weight="fill" />}
      size="md"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            className="mr-auto text-muted-foreground hover:text-destructive"
            disabled={isBusy}
            onClick={() => onAction('discard')}
          >
            {busyAction === 'discard' && (
              <CircleNotchIcon className="mr-1.5 h-4 w-4 animate-spin" />
            )}
            Discard
          </Button>
          <Button
            variant="outline"
            disabled={isBusy}
            onClick={() => onAction('finish')}
          >
            {busyAction === 'finish' && (
              <CircleNotchIcon className="mr-1.5 h-4 w-4 animate-spin" />
            )}
            Finish & create notes
          </Button>
          <Button disabled={isBusy} onClick={() => onAction('keep')}>
            {busyAction === 'keep' ? (
              <CircleNotchIcon className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <RecordIcon className="mr-1.5 h-4 w-4" weight="fill" />
            )}
            Keep recording
          </Button>
        </div>
      }
    >
      <p className="text-sm text-muted-foreground">
        Keep recording continues the same recording and notes. Finish uploads
        what was captured and writes the meeting notes.
      </p>
    </DialogPattern>
  )
}
